import { useState, useRef, useEffect } from "react";
import {
  Box, Palette, Printer, Package, Globe, Gift,
  TrendingUp, ArrowRight, CheckCircle, Star,
  Phone, Mail, MapPin, ChevronRight,
  MessageSquare, Clock, Award, Truck, Image, Quote,
} from "lucide-react";
import "./ServicesPage.css";
import { useNavigate } from "react-router-dom";

// ─── Imported bento components ────────────────────────────────
import Services  from "../components/home/Services";
import Portfolio from "../components/home/Portfolio";

// ─── useInView ────────────────────────────────────────────────
function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
}

// ─── Shared primitives ────────────────────────────────────────
function Pill({ children }) {
  return (
    <div className="bm-pill">
      <span className="bm-dot" />
      {children}
    </div>
  );
}
function SecDiv() { return <div className="sec-div" />; }
function IcArrow() { return <ArrowRight size={13} />; }
function IcChev()  { return <ChevronRight size={13} />; }


// ─── HERO ─────────────────────────────────────────────────────
function Hero() {
  const [ref, vis] = useInView(0.05);
  const navigate = useNavigate();
  return (
    <section ref={ref} className="sp-hero" aria-label="Services hero">
      <div className="sp-hero__grid" />
      <div className="sp-hero__shape sp-hero__shape--sq" />
      <div className="sp-hero__shape sp-hero__shape--dot" />

      <div className="sp-hero__inner">
        <div className={`bm-fade${vis ? " in" : ""}`}>
          <Pill>Our Services</Pill>
        </div>

        <h1 className={`sp-hero__h1 bm-fade d1${vis ? " in" : ""}`}>
          Creative Solutions That Make Brands{" "}
          <em className="sp-italic">Impossible</em>{" "}
          To Ignore
        </h1>

        <p className={`sp-hero__sub bm-fade d2${vis ? " in" : ""}`}>
          We combine design, printing, branding, signage, packaging and digital
          solutions to help businesses stand out and grow.
        </p>

        <div className={`sp-hero__btns bm-fade d3${vis ? " in" : ""}`}>
          <button className="bm-btn-r bm-btn-r--lg" onClick={() => navigate("/contact")}>
            Get Free Quote <IcArrow />
          </button>
          <button className="bm-btn-g bm-btn-g--lg" onClick={() => navigate("/portfolio")}>
            View Portfolio <IcChev />
          </button>
        </div>

        <div className={`sp-hero__stats bm-fade d4${vis ? " in" : ""}`}>
          {[
            ["100+", "Projects Done"],
            ["5+",   "Years Experience"],
            ["48h",  "Avg Turnaround"],
            ["98%",  "Happy Clients"],
          ].map(([n, l]) => (
            <div key={l} className="sp-hero__stat">
              <span className="sp-hero__stat-n">{n}</span>
              <span className="sp-hero__stat-l">{l}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


const STEPS = [
  { n: "01", Icon: MessageSquare, title: "Consultation",       desc: "We listen to your goals, audience and vision before a single pixel is drawn.",            red: false },
  { n: "02", Icon: Box,           title: "Design & Concept",   desc: "Our studio crafts tailored concepts and reviews them with you until perfect.",             red: false },
  { n: "03", Icon: Printer,       title: "Production",         desc: "Industry-grade equipment meets rigorous quality checks at every stage.",                   red: false },
  { n: "04", Icon: Truck,         title: "Delivery & Install", desc: "On-time delivery and professional installation, anywhere in Kenya.",                       red: true  },
];

function Process() {
  const [ref, vis] = useInView(0.08);
  return (
    <section ref={ref} className="sp-process" aria-label="Our process">
      <div className="sp-inner">
        <div className="sp-section-head sp-section-head--center">
          <div className={`sp-head__pill bm-fade${vis ? " in" : ""}`}>
            <Pill>How We Work</Pill>
          </div>
          <h2 className={`sp-h2 bm-fade d1${vis ? " in" : ""}`}>From Brief to Brand</h2>
          <p className={`sp-sub bm-fade d2${vis ? " in" : ""}`}>
            A clear process means no guesswork — just results, on time.
          </p>
        </div>

        <div className="bm-bento r90">
          {STEPS.map((step, i) => (
            <div
              key={step.n}
              className={`bm-bc bm-scl d${i + 1}${vis ? " in" : ""} c3 r3${step.red ? " bm-bc--red" : ""}`}
            >
              <div className="bm-bc__orb" />
              <div className="sp-step__inner">
                <div className="sp-step__top">
                  <span className="sp-step__num">{step.n}</span>
                  <div className="bm-ibox bm-ibox--sm">
                    <step.Icon size={15} color="#E31E24" strokeWidth={1.6} />
                  </div>
                </div>
                <div>
                  <div className="sp-step__title">{step.title}</div>
                  <p className="sp-step__desc">{step.desc}</p>
                </div>
                {step.red && (
                  <button className="bm-btn-r bm-btn-r--sm">
                    Get Started <IcArrow />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


const STATS = [
  { Icon: CheckCircle, n: "100+", l: "Projects Completed" },
  { Icon: Clock,       n: "24h",  l: "Response Time"      },
  { Icon: Award,       n: "5+",   l: "Years Experience"   },
  { Icon: Star,        n: "98%",  l: "Client Satisfaction"},
  { Icon: MapPin,      n: "KE",   l: "Nationwide Service" },
];

function WhyUs() {
  const [ref, vis] = useInView(0.08);
  return (
    <section ref={ref} className="sp-why" aria-label="Why choose us">
      <div className="sp-inner">
        <div className="sp-why__head">
          <div>
            <div className={`bm-fade${vis ? " in" : ""}`} style={{ marginBottom: 10 }}>
              <Pill>Why Bestmark</Pill>
            </div>
            <h2 className={`sp-h2 bm-fade d1${vis ? " in" : ""}`}>
              Numbers That Speak<br />For Themselves
            </h2>
          </div>
          <div className={`bm-fade d2${vis ? " in" : ""}`}>
            <button className="bm-btn-r">Start a Project <IcArrow /></button>
          </div>
        </div>

        <div className="bm-bento r110">
          {STATS.map((s, i) => (
            <div
              key={s.n}
              className={`bm-bc bm-scl d${i + 1}${vis ? " in" : ""} c3 r2 sp-stat`}
            >
              <s.Icon size={20} color="#E31E24" strokeWidth={1.5} />
              <div>
                <div className="sp-stat__n">{s.n}</div>
                <div className="sp-stat__l">{s.l}</div>
              </div>
            </div>
          ))}

          {/* Accent feature card */}
          <div className={`bm-bc bm-scl d6${vis ? " in" : ""} c3 r4 sp-feature`}>
            <div className="sp-feature__orb" />
            <div className="sp-feature__inner">
              <Pill>Premium Quality</Pill>
              <div>
                <p className="sp-feature__h">
                  Premium quality solutions delivered with precision and creativity.
                </p>
                <p className="sp-feature__p">
                  Every job goes through rigorous quality checks so you receive
                  something you're proud to put your name on.
                </p>
              </div>
              <button className="bm-btn-r bm-btn-r--sm">
                See Our Work <IcArrow />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── TESTIMONIALS ─────────────────────────────────────────────
const TSTS = [
  {
    name: "Amina Wanjiru", role: "CEO, Savanna Retail",
    av: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=100&q=80",
    review: "Bestmark transformed our shopfront with stunning 3D signage. Customers now stop just to photograph it. Quality exceeded every expectation.",
  },
  {
    name: "David Ochieng", role: "Director, Horizon Properties",
    av: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&q=80",
    review: "From logo to website to branded merch — Bestmark handled our full rebrand. Consistent, sharp, and delivered on time. This team genuinely cares.",
  },
  {
    name: "Grace Muthoni", role: "Founder, Bloom Bakery",
    av: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&q=80",
    review: "The packaging they designed made our products feel truly premium. Sales improved within weeks. I'd recommend Bestmark to any business.",
  },
];





// ─── PAGE ROOT ────────────────────────────────────────────────
export default function ServicesPage() {
  return (
    <>
      <a href="#sp-main" className="sp-skip">Skip to main content</a>
      <main id="sp-main">
        <Hero />
        <SecDiv />

        {/* ── Bento service cards — from components/home/Services.jsx ── */}
        <Services />

        <SecDiv />
        <Process />
        {/* <WhyUs /> */}
        <SecDiv />

        {/* ── Portfolio gallery — from components/home/Portfolio.jsx ── */}
        <Portfolio />

        <SecDiv />
       
      </main>
      
    </>
  );
}